import { useTranslation } from 'react-i18next';
import { ShapeGlyph, type ShapeGlyphVariant } from '../../components/AnnotationGlyph';
import { Icon } from '../../components/Icon';
import type { EditorTool } from '@peruselab/structure-editor/domain/editor';

interface ShapesGroupProps {
  readonly tool: EditorTool;
  readonly setTool: (tool: EditorTool) => void;
  // #413 -- the rail disclosures are a single-open accordion owned by ToolRail.
  readonly open: boolean;
  readonly onToggle: () => void;
}

const SHAPES: readonly { id: ShapeGlyphVariant; labelKey: string }[] = [
  { id: 'ellipse', labelKey: 'workspace.rail.shapes.ellipse' },
  { id: 'rect', labelKey: 'workspace.rail.shapes.rect' },
  { id: 'triangle', labelKey: 'workspace.rail.shapes.triangle' },
  { id: 'star', labelKey: 'workspace.rail.shapes.star' },
  { id: 'path', labelKey: 'workspace.rail.shapes.path' }
];

// #258 R5.3 — the left-rail Shapes disclosure, below Cycles. Each button arms `shape-${kind}`;
// the user then drags on the canvas to size the shape (DrawingPaper -> onShapePlace). Shapes
// are pure decoration (ADR 0008) -- not the molecular graph.
export function ShapesGroup({ tool, setTool, open, onToggle }: ShapesGroupProps) {
  const { t } = useTranslation();

  return (
    <details id="rail-group-shapes" className="workspace-rail-annotations" open={open}>
      <summary
        onClick={(event) => {
          event.preventDefault(); // ToolRail owns which disclosure is open
          onToggle();
        }}
      >
        {t('workspace.rail.shapes.groupLabel')}
      </summary>
      <div
        className="workspace-rail-annotations-body"
        role="group"
        aria-label={t('workspace.rail.shapes.controlsLabel')}
      >
        <div className="workspace-rail-annotations-grid">
          {/* #756 -- standalone text: stays armed; a click starts (or opens) a text annotation. */}
          <button
            type="button"
            className={`icon-button ${tool === 'text' ? 'active' : ''}`}
            aria-label={t('workspace.rail.shapes.text')}
            title={t('workspace.rail.shapes.text')}
            aria-pressed={tool === 'text'}
            onClick={() => setTool(tool === 'text' ? 'draw' : 'text')}
          >
            <Icon name="title" />
          </button>
          {SHAPES.map(({ id, labelKey }) => {
            const active = tool === `shape-${id}`;
            return (
              <button
                key={id}
                type="button"
                className={`icon-button ${active ? 'active' : ''}`}
                aria-label={t(labelKey)}
                title={t(labelKey)}
                aria-pressed={active}
                onClick={() => setTool(active ? 'draw' : `shape-${id}`)}
              >
                <ShapeGlyph variant={id} />
              </button>
            );
          })}
        </div>
        <p className="workspace-rail-annotations-hint">{t('workspace.rail.shapes.hint')}</p>
      </div>
    </details>
  );
}
